// Insured-person repeater — add/edit/remove Spouse, Father, Mother, Son(s),
// Daughter(s). Name, Relationship, DOB (compulsory), auto-calculated Age, Gender.
import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, Trash2, UserPlus } from "lucide-react";
import { calcAge } from "./FieldRenderer";

export default function RepeaterField({ field, value, onChange, readonly = false, testid = "gi-persons" }) {
  const persons = Array.isArray(value) ? value : [];
  const relationships = field?.repeater?.relationships?.length ? field.repeater.relationships : ["Spouse", "Father", "Mother", "Son", "Daughter"];
  const setP = (i, k, v) => onChange(persons.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const add = () => onChange([...persons, { name: "", relationship: "", dob: "", gender: "" }]);

  return (
    <div data-testid={testid}>
      <div className="space-y-3">
        {persons.map((p, i) => (
          <div key={i} className="rounded-lg border border-border p-3.5 bg-muted/20" data-testid={`${testid}-row-${i}`}>
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-xs font-bold text-muted-foreground">Insured Person #{i + 1}</span>
              {!readonly && (
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => onChange(persons.filter((_, j) => j !== i))} data-testid={`${testid}-del-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Name of Insured <span className="text-red-600">*</span></Label>
                <Input value={p.name || ""} disabled={readonly} onChange={e => setP(i, "name", e.target.value)} data-testid={`${testid}-${i}-name`} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Relationship <span className="text-red-600">*</span></Label>
                <Select value={p.relationship || ""} onValueChange={v => setP(i, "relationship", v)} disabled={readonly}>
                  <SelectTrigger data-testid={`${testid}-${i}-relationship`}><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {relationships.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Date of Birth <span className="text-red-600">*</span></Label>
                <div className="flex items-center gap-2">
                  <Input type="date" value={p.dob || ""} disabled={readonly} onChange={e => setP(i, "dob", e.target.value)} data-testid={`${testid}-${i}-dob`} />
                  <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap" data-testid={`${testid}-${i}-age`}>
                    {p.dob ? `Age: ${calcAge(p.dob)}` : "Age: —"}
                  </span>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold">Gender <span className="text-red-600">*</span></Label>
                <RadioGroup value={p.gender || ""} onValueChange={v => setP(i, "gender", v)} className="flex gap-3 pt-1.5" data-testid={`${testid}-${i}-gender`}>
                  {["Male", "Female", "Other"].map(g => (
                    <label key={g} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <RadioGroupItem value={g} disabled={readonly} data-testid={`${testid}-${i}-gender-${g.toLowerCase()}`} /> {g}
                    </label>
                  ))}
                </RadioGroup>
              </div>
            </div>
          </div>
        ))}
      </div>
      {!readonly && (
        <Button type="button" variant="outline" size="sm" className="mt-3" onClick={add} data-testid={`${testid}-add`}>
          <UserPlus className="w-4 h-4 mr-1.5" /> Add Insured Person
        </Button>
      )}
      {!persons.length && readonly && <p className="text-sm text-muted-foreground py-2">No insured persons added.</p>}
    </div>
  );
}

// Read-only summary table (Name, Relationship, DOB, auto Age, Gender)
export function PersonsSummary({ persons, member, testid = "gi-persons-summary" }) {
  const rows = [
    member ? { name: member.name, relationship: "Self (KELTA Member)", dob: member.dob, gender: member.gender } : null,
    ...(persons || []),
  ].filter(Boolean);
  return (
    <div className="overflow-x-auto rounded-lg border border-border" data-testid={testid}>
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-teal-800 text-white text-xs">
            <th className="px-3 py-2 text-left">Name</th>
            <th className="px-3 py-2 text-left">Relationship</th>
            <th className="px-3 py-2 text-left">Date of Birth</th>
            <th className="px-3 py-2 text-left">Age</th>
            <th className="px-3 py-2 text-left">Gender</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p, i) => (
            <tr key={i} className={i % 2 ? "bg-muted/40" : ""} data-testid={`${testid}-row-${i}`}>
              <td className="px-3 py-2 font-medium">{p.name || "—"}</td>
              <td className="px-3 py-2">{p.relationship || "—"}</td>
              <td className="px-3 py-2">{p.dob || "—"}</td>
              <td className="px-3 py-2">{p.dob ? calcAge(p.dob) : "—"}</td>
              <td className="px-3 py-2">{p.gender || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
